import { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { defineMessages, IntlShape } from 'react-intl';
import {
  parseReference,
  findCoding,
  findContactPoint,
  findHumanName,
} from '@lifeomic/fhir-constants';
import {
  getNameFromFHIRRecord,
  getPractitionerDisplayName,
} from '@lifeomic/phc-web-toolkit/src/util/fhir';
import dayjs from 'dayjs';
import { fhirSystems } from '../pages/OnboardingSnapshot/fhir-systems';
import {
  getElationPatientChartUrl,
  getElationPatientId,
} from '../../util/elation';

const messages = defineMessages({
  pageTitle: {
    id: 'OnboardingSnapshot.pageTitle',
    defaultMessage: 'Onboarding Snapshot',
  },
  cohort: {
    id: 'OnboardingSnapshot.cohort',
    defaultMessage: 'Cohort',
  },
  status: {
    id: 'OnboardingSnapshot.status',
    defaultMessage: 'Status',
  },
  inProgress: {
    id: 'OnboardingSnapshot.inProgress',
    defaultMessage: 'In Progress',
  },
  inProgressSubtitle: {
    id: 'OnboardingSnapshot.inProgressSubtitle',
    defaultMessage: 'includes those not started',
  },
  completed: {
    id: 'OnboardingSnapshot.completed',
    defaultMessage: 'Completed',
  },
  settingUp: {
    id: 'OnboardingSnapshot.settingUp',
    defaultMessage: 'Setting up...',
  },
  targetingMembers: {
    id: 'OnboardingSnapshot.targetingMembers',
    defaultMessage: 'Targeting {count} Members',
  },
  missing: {
    id: 'OnboardingSnapshot.missing',
    defaultMessage: '<missing>',
  },
  elationIdNotFound: {
    id: 'OnboardingSnapshot.elationIdNotFound',
    defaultMessage: 'elation-id-not-found',
  },
  goToElationChart: {
    id: 'OnboardingSnapshot.goToElationChart',
    defaultMessage: 'Go To Elation Chart',
  },
  goToPatient: {
    id: 'OnboardingSnapshot.goToPatient',
    defaultMessage: 'Go To Patient',
  },
  viewDetails: {
    id: 'OnboardingSnapshot.viewDetails',
    defaultMessage: 'View Onboarding Details',
  },
});

const normalizeTag = (tag: string) => tag.toLowerCase().replace(/\s+/g, '');

export const formatMember = (
  patient: WithPlatformDefaults<fhir3.Patient>,
  {
    formatMessage,
    providers,
    centerName,
  }: {
    formatMessage: IntlShape['formatMessage'];
    providers: Array<WithPlatformDefaults<fhir3.Practitioner>>;
    centerName?: string;
  },
) => {
  const name = getNameFromFHIRRecord(patient.name);
  const preferredName = findHumanName(patient.name, {
    use: 'nickname',
  });
  const email =
    findContactPoint(patient.telecom, {
      system: 'email',
    })?.value || formatMessage(messages.missing);
  const phone =
    findContactPoint(patient.telecom, {
      system: 'phone',
      use: 'mobile',
    })?.value ||
    findContactPoint(patient.telecom, {
      system: 'phone',
    })?.value ||
    formatMessage(messages.missing);

  const tier = findCoding(patient?.meta?.tag, {
    system: fhirSystems.membershipTier.system,
  })?.code;

  const elationChartUrl = getElationPatientChartUrl(
    getElationPatientId(patient),
  );

  const provider = providers.find(provider =>
    // General practitioner refs may include health coach - so the practitioners
    // passed should be filtered to those with physician role (tag coding)
    patient.generalPractitioner?.some(
      ref => parseReference(ref)?.id === provider.id,
    ),
  );

  const providerName = getPractitionerDisplayName(
    provider || {
      resourceType: 'Practitioner',
    },
    {
      excludeQualifications: true,
      defaultName: formatMessage(messages.missing),
    },
  );

  const [careCoordinator] = patient.meta.tag
    ?.filter(
      tag =>
        tag.system === fhirSystems.patient.elationTag.system &&
        normalizeTag(tag.code || '').startsWith('cc-'),
    )
    // Sort by longest, assuming the longest is the most specific
    .sort((a, b) => (b.code || '').length - (a.code || '').length);

  const [, careCoordinatorName = formatMessage(messages.missing)] =
    careCoordinator?.code?.split('-') || [];

  const [nursingStaff] = patient.meta.tag
    ?.filter(
      tag =>
        tag.system === fhirSystems.patient.elationTag.system &&
        ['ma-', 'rn-', 'dnp-', 'np-'].some(prefix =>
          normalizeTag(tag.code || '').startsWith(prefix),
        ),
    )
    .sort((a, b) => (b.code || '').length - (a.code || '').length);

  const [, nursingStaffName = formatMessage(messages.missing)] =
    nursingStaff?.code?.split('-') || [];

  return {
    name: [name.given, name.family].filter(Boolean).join(' '),
    dateOfBirth: patient.birthDate
      ? dayjs(patient.birthDate).format('MM/DD/YYYY')
      : '<missing>',
    gender: patient.gender
      ? patient.gender.charAt(0).toUpperCase() +
        patient.gender.slice(1).toLowerCase()
      : '',
    preferredName: [...(preferredName?.given || []), preferredName?.family]
      .filter(Boolean)
      .join(' '),
    email,
    phone,
    elationId: getElationPatientId(patient),
    tier,
    elationChartUrl,
    providerName:
      providerName.trim().toLowerCase() || formatMessage(messages.missing),
    careCoordinatorName:
      careCoordinatorName.trim() || formatMessage(messages.missing),
    nursingStaffName:
      nursingStaffName.trim() || formatMessage(messages.missing),
    centerName: centerName || formatMessage(messages.missing),
  };
};

export type Member = ReturnType<typeof formatMember>;

export const isReferenceMatch = ({
  reference,
  patientId,
}: {
  reference?: fhir3.Reference;
  patientId: string;
}): boolean => parseReference(reference)?.id === patientId;
